import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Download, Minus, Plus, X } from 'lucide-react';

export function ReHubBeforeAfterSlider({
  before,
  after,
  beforeLabel = 'Original',
  afterLabel = 'Enhanced',
}: {
  before: string;
  after: string;
  beforeLabel?: string;
  afterLabel?: string;
}) {
  const [position, setPosition] = useState(50);
  const frame = useRef<HTMLDivElement>(null);

  const moveTo = (clientX: number) => {
    const rect = frame.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const next = ((clientX - rect.left) / rect.width) * 100;
    setPosition(Math.min(98, Math.max(2, next)));
  };

  return (
    <div
      ref={frame}
      className="relative h-full w-full touch-none select-none overflow-hidden"
      onPointerDown={(event) => {
        (event.currentTarget as HTMLDivElement).setPointerCapture(event.pointerId);
        moveTo(event.clientX);
      }}
      onPointerMove={(event) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) moveTo(event.clientX);
      }}
    >
      <img src={after} alt={afterLabel} className="h-full w-full object-contain" draggable={false} />
      <img
        src={before}
        alt={beforeLabel}
        className="absolute inset-0 h-full w-full object-contain"
        style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
        draggable={false}
      />
      <div className="absolute inset-y-0" style={{ left: `${position}%` }}>
        <div className="absolute inset-y-0 -translate-x-1/2 border-l border-white/80" />
        <button
          type="button"
          role="slider"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(position)}
          aria-label="Compare original and enhanced"
          className="absolute top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-slate-900 shadow-lg"
          onKeyDown={(event) => {
            if (event.key === 'ArrowLeft') setPosition((value) => Math.max(2, value - 4));
            if (event.key === 'ArrowRight') setPosition((value) => Math.min(98, value + 4));
          }}
        >
          <ChevronLeft size={14} />
          <ChevronRight size={14} />
        </button>
      </div>
      <span className="absolute left-3 top-3 rounded-full bg-black/60 px-2 py-1 text-[11px] font-medium">{beforeLabel}</span>
      <span className="absolute right-3 top-3 rounded-full bg-black/60 px-2 py-1 text-[11px] font-medium">{afterLabel}</span>
    </div>
  );
}

export function ReHubImageViewer({
  open,
  title,
  imageUrl,
  originalUrl,
  onClose,
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false,
  onDownload,
  downloading = false,
  extra,
  footer,
}: {
  open: boolean;
  title: string;
  imageUrl?: string | null;
  originalUrl?: string | null;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
  onDownload?: () => void;
  downloading?: boolean;
  extra?: React.ReactNode;
  footer?: React.ReactNode;
}) {
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [compare, setCompare] = useState(false);
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const canCompare = Boolean(imageUrl && originalUrl);

  useEffect(() => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    setCompare(false);
  }, [imageUrl, open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft' && hasPrev) onPrev?.();
      if (event.key === 'ArrowRight' && hasNext) onNext?.();
      if (event.key === '+' || event.key === '=') setZoom((value) => Math.min(4, value + 0.25));
      if (event.key === '-' || event.key === '_') setZoom((value) => Math.max(1, value - 0.25));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, hasPrev, hasNext, onClose, onPrev, onNext]);

  if (!open) return null;

  return (
    <div className="rehub-dash fixed inset-0 z-[80] flex flex-col bg-black/95" role="dialog" aria-modal="true" aria-label={title}>
      <div className="flex items-center gap-2 border-b border-white/10 px-3 py-3 sm:px-5">
        <h2 className="min-w-0 flex-1 truncate text-sm font-medium">{title}</h2>
        {canCompare && (
          <button type="button" className={`rounded-lg px-3 py-1.5 text-sm ${compare ? 'bg-orange-500 text-white' : 'bg-white/10 text-slate-200'}`} onClick={() => { setCompare((value) => !value); setZoom(1); setOffset({ x: 0, y: 0 }); }}>
            Compare
          </button>
        )}
        {!compare && (
          <div className="flex items-center gap-1">
            <button type="button" aria-label="Zoom out" className="rounded-lg bg-white/10 p-1.5" onClick={() => setZoom((value) => Math.max(1, value - 0.25))}><Minus size={16} /></button>
            <span className="w-12 text-center text-xs text-slate-300">{Math.round(zoom * 100)}%</span>
            <button type="button" aria-label="Zoom in" className="rounded-lg bg-white/10 p-1.5" onClick={() => setZoom((value) => Math.min(4, value + 0.25))}><Plus size={16} /></button>
          </div>
        )}
        {onDownload && (
          <button type="button" onClick={onDownload} disabled={downloading} className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-sm disabled:opacity-50">
            <Download size={14} /> Download
          </button>
        )}
        {extra}
        <button type="button" onClick={onClose} aria-label="Close viewer" className="rounded-lg bg-white/10 p-1.5"><X size={16} /></button>
      </div>
      <div className="relative min-h-0 flex-1">
        {hasPrev && (
          <button type="button" aria-label="Previous image" className="absolute left-3 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/10 p-3 hover:bg-white/20" onClick={onPrev}>
            <ChevronLeft size={22} />
          </button>
        )}
        {hasNext && (
          <button type="button" aria-label="Next image" className="absolute right-3 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/10 p-3 hover:bg-white/20" onClick={onNext}>
            <ChevronRight size={22} />
          </button>
        )}
        <div className="flex h-full items-center justify-center p-4 sm:p-8">
          {compare && canCompare ? (
            <div className="h-full max-h-full w-full max-w-6xl">
              <ReHubBeforeAfterSlider before={originalUrl!} after={imageUrl!} />
            </div>
          ) : imageUrl ? (
            <img
              src={imageUrl}
              alt={title}
              draggable={false}
              className="max-h-full max-w-full object-contain"
              style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`, cursor: zoom > 1 ? 'grab' : 'default' }}
              onPointerDown={(event) => {
                if (zoom <= 1) return;
                drag.current = { x: event.clientX, y: event.clientY, ox: offset.x, oy: offset.y };
                (event.currentTarget as HTMLImageElement).setPointerCapture(event.pointerId);
              }}
              onPointerMove={(event) => {
                if (!drag.current) return;
                setOffset({
                  x: drag.current.ox + event.clientX - drag.current.x,
                  y: drag.current.oy + event.clientY - drag.current.y,
                });
              }}
              onPointerUp={() => { drag.current = null; }}
            />
          ) : (
            <p className="text-sm text-slate-400">This image isn’t available.</p>
          )}
        </div>
      </div>
      {footer && <div className="border-t border-white/10 px-4 py-3">{footer}</div>}
    </div>
  );
}
